import { isSinglesUnderSelection } from "../../../supabase/functions/_shared/singles-market-policy.ts";
// Singles Chain workspace. Data qualification and financial actions remain in their existing hooks.
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSinglesSession } from "../../singles/useSinglesSession.ts";
import { useSinglesReserve } from "../../singles/useSinglesReserve.ts";
import { useSinglesChain } from "../../singles/useSinglesChain.ts";
import { useBustNotifications } from "../../singles/useBustNotifications.ts";
import { useDoctrinePicks } from "../../doctrine/useDoctrinePicks.ts";
import { DoctrineSection } from "../../components/DoctrineSection.tsx";
import {
  SetSeedStakeModal,
  CooldownCard,
  BustNotificationBanner,
  isReserveUnseeded,
} from "../../components/singles-extras/index.ts";
import { Icon } from "../../components/icons/Icon.tsx";
import {
  SinglesWorkspaceNav,
  SinglesBoard,
  SinglesReserveAside,
  SinglesActivity,
  SinglesReserveHistory,
  SinglesBankedWins,
  singlesDate,
  singlesStatus,
  type SinglesView,
} from "./SinglesWorkspace.tsx";
import "../../styles/powerball-workspace.css";
import "./singles-workspace.css";

export function SinglesPage(): JSX.Element {
  const {
    session,
    slots,
    sessionDate,
    loading: sLoading,
    error: sError,
    refetch: refetchSession,
  } = useSinglesSession();
  const {
    reserve,
    transactions,
    loading: rLoading,
    error: rError,
    refetch: refetchReserve,
  } = useSinglesReserve();
  const {
    chains,
    loading: cLoading,
    error: cError,
    cooldown,
    refetch: refetchChain,
  } = useSinglesChain();
  const {
    notifications: bustNotifications,
    error: bustError,
    acknowledge: ackBustNotification,
    refetch: refetchBusts,
  } = useBustNotifications();
  const [params, setParams] = useSearchParams();
  const view: SinglesView =
    params.get("view") === "activity"
      ? "activity"
      : params.get("view") === "reserve"
        ? "reserve"
        : "today";
  const [seedModalOpen, setSeedModalOpen] = useState(false);
  const refetchAll = () => {
    refetchSession();
    refetchReserve();
    refetchChain();
    refetchBusts();
  };
  const openReserve = () =>
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set("view", "reserve");
      return next;
    });
  const filledSlots = useMemo(() => slots.filter((slot) => slot.fixture_id), [slots]);
  const settled = filledSlots.filter((slot) => slot.outcome && slot.outcome !== "pending").length;
  const nextKickoff = filledSlots
    .filter(
      (slot) =>
        (!slot.outcome || slot.outcome === "pending") &&
        Date.parse(slot.kickoff_at ?? "") > Date.now(),
    )
    .sort((a, b) => Date.parse(a.kickoff_at!) - Date.parse(b.kickoff_at!))[0]?.kickoff_at;
  const noLivePicks = !sLoading && !sError && (!session || session.status === "no_fixtures");
  const { probabilityPicks, valuePicks, substrateEmpty, substrateWeek } = useDoctrinePicks({
    t1HasPicks: !noLivePicks,
  });
  const effectiveCooldownEndsAt =
    cooldown?.cooldown_ends_at ??
    chains.find(
      (chain) => chain.status === "busted" && Date.parse(chain.cooldown_ends_at ?? "") > Date.now(),
    )?.cooldown_ends_at ??
    bustNotifications.find(
      (notification) => Date.parse(notification.cooldown_ends_at ?? "") > Date.now(),
    )?.cooldown_ends_at ??
    null;
  const latestBust = bustNotifications[0] ?? null;
  const unseeded = isReserveUnseeded(reserve);
  const date = sessionDate || session?.session_date;
  const issues = [
    sError ? "Today's selection feed could not refresh." : null,
    rError ? "Your reserve could not refresh." : null,
    cError && cError !== "cooldown_active" ? "Your chain activity could not refresh." : null,
    bustError ? "Chain notifications could not refresh." : null,
  ].filter(Boolean);

  return (
    <main className="fp pb-workspace singles-workspace">
      <SinglesWorkspaceNav active={view} />
      <header className="pb-workspace-header">
        <div>
          <span className="pb-eyebrow">DAILY FOOTBALL INTELLIGENCE</span>
          <h1>SINGLES CHAIN</h1>
          <p>A clear view of today's selections and your personal chain record.</p>
        </div>
        <button
          type="button"
          className="pb-secondary-button"
          onClick={refetchAll}
          aria-label="Refresh Singles Chain"
        >
          <Icon name="refresh" size={14} />
          Refresh
        </button>
      </header>
      <div className="singles-session-strip">
        <div>
          <span>Session</span>
          <strong>
            {date ? singlesDate(date, false) : sLoading ? "Loading…" : "Awaiting session"}
          </strong>
        </div>
        <div>
          <span>Publication</span>
          <strong>
            {sError
              ? "Refresh unavailable"
              : sLoading && !session
                ? "Loading…"
                : session?.policy_blocked
                  ? "Market restricted"
                  : singlesStatus(session?.status)}
          </strong>
        </div>
        <div>
          <span>Tracking</span>
          <strong>
            {session ? (session.simulated ? "Paper tracking" : "Personal tracking") : "—"}
          </strong>
        </div>
        <div>
          <span>Time zone</span>
          <strong>Accra · GMT</strong>
        </div>
      </div>
      {!!issues.length && (
        <div className="pb-notice singles-notice" role="alert">
          {issues.join(" ")} Any retained records remain visible. Use Refresh to try again.
        </div>
      )}
      {latestBust && (
        <BustNotificationBanner
          notification={latestBust}
          onDismiss={(id) => {
            void ackBustNotification(id);
          }}
        />
      )}
      {effectiveCooldownEndsAt && (
        <CooldownCard
          cooldownEndsAt={effectiveCooldownEndsAt}
          capitalAfter={latestBust?.capital_after ?? null}
          stakeSource={latestBust?.stake_source ?? null}
          message={latestBust?.message ?? null}
        />
      )}
      {view === "today" && (
        <>
          <div className="singles-board-heading">
            <div>
              <h2>The daily board</h2>
              <p>
                {sLoading && !filledSlots.length
                  ? "Loading today's publication…"
                  : sError && !filledSlots.length
                    ? "Today's publication is unavailable."
                    : `${filledSlots.length} published ${filledSlots.length === 1 ? "selection" : "selections"} · ${settled} settled`}
              </p>
            </div>
            <span>
              {nextKickoff
                ? `Next kickoff ${new Intl.DateTimeFormat("en-GB", { timeZone: "Africa/Accra", hour: "2-digit", minute: "2-digit" }).format(new Date(nextKickoff))}`
                : "Results remain pending until verified"}
            </span>
          </div>
          <div className="singles-workspace-layout">
            <div className="singles-workspace-content">
              <SinglesBoard
                slots={filledSlots}
                status={session?.status}
                policyBlocked={session?.policy_blocked}
                loading={sLoading}
                error={!!sError}
                fallback={
                  noLivePicks ? (
                    <div className="singles-fallback">
                      <DoctrineSection
                        probabilityPicks={probabilityPicks.filter(
                          (pick) => !isSinglesUnderSelection(pick.market, pick.selection),
                        )}
                        valuePicks={valuePicks.filter(
                          (pick) => !isSinglesUnderSelection(pick.market, pick.selection),
                        )}
                        isFullFallback
                        substrateEmpty={substrateEmpty}
                        substrateWeek={substrateWeek}
                      />
                    </div>
                  ) : undefined
                }
              />
              <SinglesBankedWins chains={chains} />
            </div>
            <SinglesReserveAside
              reserve={reserve}
              loading={rLoading}
              error={!!rError}
              unseeded={unseeded}
              session={session}
              onSeed={() => setSeedModalOpen(true)}
              onReserve={openReserve}
            />
          </div>
        </>
      )}
      {view === "activity" && (
        <SinglesActivity
          chains={chains}
          loading={cLoading}
          error={!!cError && cError !== "cooldown_active"}
        />
      )}
      {view === "reserve" && (
        <SinglesReserveHistory
          reserve={reserve}
          transactions={transactions}
          loading={rLoading}
          error={!!rError}
          unseeded={unseeded}
          onSeed={() => setSeedModalOpen(true)}
        />
      )}
      <footer className="pb-workspace-footer">
        <span>Intelligence, not certainty. No bets are placed here.</span>
        <span>Prices are recorded references · Accra time</span>
      </footer>
      <SetSeedStakeModal
        open={seedModalOpen}
        onClose={() => setSeedModalOpen(false)}
        onSeeded={refetchAll}
      />
    </main>
  );
}
